// 3MF для Bambu Studio / Orca на основе исходного файла: настройки проекта (пластики, принтер, профиль)
// остаются как были, меняются только сетки и раскладка деталей. Без этого Bambu пишет «invalid config»
// и открывает одну геометрию — без цветов.
import { unzipSync } from 'fflate';
import { zip } from '@gen3d/core';

/** Деталь объекта: сетка в координатах объекта, покраска по треугольникам (номер пластика), пластик детали. */
export type BPart = { name: string; pos: Float32Array; tri: Uint32Array; paint?: Uint8Array; ext: number };
/** Объект на столе: детали и сдвиги копий (мм, координаты стола). */
export type BObject = { name: string; parts: BPart[]; at: [number, number, number][] };

/** Сетки, раскладка и всё, что ссылается на старые номера объектов, пишутся заново; остальное — как в исходнике. */
const DROP = /^(3D\/|Metadata\/model_settings\.config$|Metadata\/cut_information\.xml$|Metadata\/layer_heights_profile\.txt$|Metadata\/layer_config_ranges\.xml$|Metadata\/.*\.gcode(\.md5)?$|Metadata\/plate_\d+\.json$)/;

const esc = (s: string) => s.replace(/[<>&"]/g, (ch) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' })[ch]!);
const f4 = (x: number) => String(Math.round(x * 1e4) / 1e4);
/** Код покраски Bambu для треугольника целиком одним пластиком (как в @gen3d/core). */
const paintCode = (f: number) => (f < 1 ? '' : f < 3 ? String(f << 2) : (f - 3).toString(16).toUpperCase() + 'C');
const NS = 'xmlns="http://schemas.microsoft.com/3dmanufacturing/core/2015/02" xmlns:BambuStudio="http://schemas.bambulab.com/package/2021" xmlns:p="http://schemas.microsoft.com/3dmanufacturing/production/2015/06" requiredextensions="p"';

/** Исходник — проект Bambu / Orca (есть настройки проекта): тогда и выгружать в его формате. */
export function isBambu(src: Uint8Array) {
  try { return !!unzipSync(src, { filter: (f) => f.name === 'Metadata/project_settings.config' })['Metadata/project_settings.config']; } catch { return false; }
}

export async function toBambu3MF(src: Uint8Array, objects: BObject[], plate: number): Promise<Blob> {
  const files = unzipSync(src);
  const dec = new TextDecoder();
  const text = (n: string) => (files[n] ? dec.decode(files[n]) : '');

  // сетки — в одном файле объектов, как делает Bambu (3D/Objects/…)
  const OBJ = '3D/Objects/object_1.model';
  const mesh: string[] = [`<?xml version="1.0" encoding="UTF-8"?>\n<model unit="millimeter" xml:lang="en-US" ${NS}>\n <metadata name="BambuStudio:3mfVersion">1</metadata>\n <resources>\n`];
  let id = 0;
  const partId: number[][] = objects.map((o) => o.parts.map((p) => {
    id++;
    let buf: string[] = [`  <object id="${id}" type="model"><mesh><vertices>`];
    const flush = () => { mesh.push(buf.join('')); buf = []; };
    for (let i = 0; i < p.pos.length; i += 3) { buf.push(`<vertex x="${f4(p.pos[i])}" y="${f4(p.pos[i + 1])}" z="${f4(p.pos[i + 2])}"/>`); if (buf.length > 20000) flush(); }
    buf.push('</vertices><triangles>');
    for (let i = 0; i < p.tri.length; i += 3) {
      // покраска пластиком самой детали не нужна: деталь и так им печатается
      const f = p.paint ? p.paint[i / 3] : 0, pc = f && f !== p.ext ? paintCode(f) : '';
      buf.push(`<triangle v1="${p.tri[i]}" v2="${p.tri[i + 1]}" v3="${p.tri[i + 2]}"${pc ? ` paint_color="${pc}"` : ''}/>`);
      if (buf.length > 20000) flush();
    }
    buf.push('</triangles></mesh></object>\n');
    flush();
    return id;
  }));
  mesh.push(' </resources>\n <build/>\n</model>\n');

  // главный файл: метаданные исходника (Application — по нему Bambu узнаёт свой проект), объекты-сборки, стол
  const head = (text('3D/3dmodel.model').match(/<metadata [\s\S]*?<\/metadata>|<metadata [^>]*\/>/g) ?? [])
    .filter((m) => !/name="(Thumbnail_Middle|Thumbnail_Small)"/.test(m)).map((m) => ' ' + m).join('\n');
  const asm: string[] = [], items: string[] = [], inst: string[] = [], conf: string[] = [];
  let ident = 1;
  objects.forEach((o, k) => {
    const oid = id + 1 + k;
    asm.push(`  <object id="${oid}" type="model"><components>${partId[k].map((pid) => `<component p:path="/${OBJ}" objectid="${pid}" transform="1 0 0 0 1 0 0 0 1 0 0 0"/>`).join('')}</components></object>`);
    o.at.forEach((a, n) => {
      items.push(`  <item objectid="${oid}" transform="1 0 0 0 1 0 0 0 1 ${f4(a[0])} ${f4(a[1])} ${f4(a[2])}" printable="1"/>`);
      inst.push(`    <model_instance>\n      <metadata key="object_id" value="${oid}"/>\n      <metadata key="instance_id" value="${n}"/>\n      <metadata key="identify_id" value="${ident++}"/>\n    </model_instance>`);
    });
    conf.push(`  <object id="${oid}">\n    <metadata key="name" value="${esc(o.name)}"/>\n    <metadata key="extruder" value="${o.parts[0]?.ext ?? 1}"/>\n` +
      o.parts.map((p, j) => `    <part id="${partId[k][j]}" subtype="normal_part">\n      <metadata key="name" value="${esc(p.name)}"/>\n      <metadata key="matrix" value="1 0 0 0 0 1 0 0 0 0 1 0 0 0 0 1"/>\n      <metadata key="extruder" value="${p.ext}"/>\n    </part>`).join('\n') +
      '\n  </object>');
  });
  const model = `<?xml version="1.0" encoding="UTF-8"?>\n<model unit="millimeter" xml:lang="en-US" ${NS}>\n${head}\n <resources>\n${asm.join('\n')}\n </resources>\n <build>\n${items.join('\n')}\n </build>\n</model>\n`;

  // стол: настройки выбранного стола исходника (пластики по соплам, имя), без устаревших картинок
  const src0 = text('Metadata/model_settings.config');
  const plates = [...src0.matchAll(/<plate>([\s\S]*?)<\/plate>/g)].map((m) => m[1]);
  const pick = plates.find((b) => new RegExp(`key="plater_id" value="${plate}"`).test(b)) ?? plates[0] ?? '';
  const keep = (pick.split('<model_instance')[0].match(/<metadata key="[^"]+" value="[^"]*"\s*\/>/g) ?? [])
    .filter((m) => !/key="(plater_id|thumbnail_file|thumbnail_no_light_file|top_file|pick_file)"/.test(m))
    .map((m) => '    ' + m).join('\n');
  const settings = `<?xml version="1.0" encoding="UTF-8"?>\n<config>\n${conf.join('\n')}\n  <plate>\n    <metadata key="plater_id" value="1"/>\n${keep ? keep + '\n' : ''}${inst.join('\n')}\n  </plate>\n</config>\n`;

  const out: { name: string; data: Uint8Array | string | Blob }[] = [];
  for (const [n, d] of Object.entries(files)) if (!n.endsWith('/') && !DROP.test(n)) out.push({ name: n, data: d });
  out.push(
    { name: '3D/3dmodel.model', data: model },
    { name: '3D/_rels/3dmodel.model.rels', data: `<?xml version="1.0" encoding="UTF-8"?>\n<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">\n <Relationship Target="/${OBJ}" Id="rel-1" Type="http://schemas.microsoft.com/3dmanufacturing/2013/01/3dmodel"/>\n</Relationships>\n` },
    { name: OBJ, data: new Blob(mesh, { type: 'application/xml' }) },
    { name: 'Metadata/model_settings.config', data: settings },
  );
  // без [Content_Types] и .rels пакет не откроется — у исходника они есть всегда, но на всякий случай
  if (!files['[Content_Types].xml']) out.push({ name: '[Content_Types].xml', data: '<?xml version="1.0" encoding="UTF-8"?>\n<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="model" ContentType="application/vnd.ms-package.3dmanufacturing-3dmodel+xml"/><Default Extension="png" ContentType="image/png"/></Types>' });
  return zip(out, 'model/3mf');
}
